<template>
  <div class="oa-dashboard">
    <!-- KPI Cards -->
    <div class="oa-kpi-row">
      <div class="oa-kpi-card">
        <div class="oa-kpi-icon oa-kpi-icon-blue">
          <i class="fa fa-calendar-check-o"></i>
        </div>
        <div class="oa-kpi-content">
          <div class="oa-kpi-value">{{ stats.totalBookings }}</div>
          <div class="oa-kpi-label">Total Bookings</div>
        </div>
      </div>
      <div class="oa-kpi-card">
        <div class="oa-kpi-icon oa-kpi-icon-green">
          <i class="fa fa-user-plus"></i>
        </div>
        <div class="oa-kpi-content">
          <div class="oa-kpi-value">{{ stats.newPatients }}</div>
          <div class="oa-kpi-label">New Patients</div>
        </div>
      </div>
      <div class="oa-kpi-card">
        <div class="oa-kpi-icon oa-kpi-icon-purple">
          <i class="fa fa-calendar"></i>
        </div>
        <div class="oa-kpi-content">
          <div class="oa-kpi-value">{{ stats.thisMonth }}</div>
          <div class="oa-kpi-label">This Month</div>
        </div>
      </div>
    </div>

    <!-- Bookings Table Section -->
    <div class="oa-section">
      <div class="oa-section-header">
        <h2><i class="fa fa-list"></i> Your Online Bookings</h2>
      </div>

      <!-- Filters Row -->
      <div class="oa-dashboard-filters">
        <div class="oa-dashboard-search">
          <i class="fa fa-search"></i>
          <input
            type="text"
            class="oa-form-input"
            placeholder="Search by name or phone..."
            v-model="searchQuery"
            @input="onSearchInput"
          />
          <button v-if="searchQuery" class="oa-search-clear" @click="clearSearch">
            <i class="fa fa-times"></i>
          </button>
        </div>

        <select class="oa-form-input oa-filter-select" v-model="filterType" @change="loadBookings(1)">
          <option value="">All Types</option>
          <option v-for="t in appointmentTypes" :key="t.id" :value="t.id">{{ t.name }}</option>
        </select>

        <select class="oa-form-input oa-filter-select" v-model="filterProvider" @change="loadBookings(1)">
          <option value="">All Providers</option>
          <option v-for="p in providers" :key="p.ProvNum" :value="p.ProvNum">
            {{ p.IsSecondary === 0 ? 'Dr. ' : '' }}{{ p.FName }} {{ p.LName }}
          </option>
        </select>

        <select class="oa-form-input oa-filter-select" v-model="filterSource" @change="loadBookings(1)">
          <option value="">All Sources</option>
          <option value="direct">Direct</option>
          <option v-for="s in uniqueSources" :key="s" :value="s">{{ s }}</option>
        </select>

        <div class="oa-date-filter">
          <span class="oa-date-label">From</span>
          <input type="date" class="form-control form-control-sm oa-date-input" v-model="dateFrom" @change="loadBookings(1)" />
          <span class="oa-date-label">To</span>
          <input type="date" class="form-control form-control-sm oa-date-input" v-model="dateTo" @change="loadBookings(1)" />
        </div>
      </div>

      <!-- Loading -->
      <div v-if="loadingBookings" class="oa-dashboard-loading">
        <i class="fa fa-spinner fa-spin"></i> Loading bookings...
      </div>

      <!-- Empty State -->
      <div v-else-if="bookings.length === 0" class="oa-empty-state">
        <i class="fa fa-calendar-o"></i>
        <p>No online bookings found</p>
        <p class="small">Bookings will appear here once patients schedule appointments online.</p>
      </div>

      <!-- Table -->
      <div v-else class="oa-dashboard-table-wrapper">
        <table class="oa-dashboard-table">
          <thead>
            <tr>
              <th>Patient</th>
              <th>Type</th>
              <th>Appointment Date</th>
              <th>Time</th>
              <th>Provider</th>
              <th>Patient Type</th>
              <th>Source</th>
              <th>Booked On</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="b in bookings" :key="b.BookingID">
              <td>
                <div class="oa-booking-patient">
                  <strong>{{ b.FirstName }} {{ b.LastName }}</strong>
                  <span v-if="b.Phone" class="oa-booking-phone">{{ b.Phone }}</span>
                </div>
              </td>
              <td>{{ b.AppointmentTypeName || '—' }}</td>
              <td>{{ formatDate(b.AppointmentDate) }}</td>
              <td>{{ formatTime(b.AppointmentTime) }}</td>
              <td>{{ b.ProviderName || '—' }}</td>
              <td>
                <span class="oa-badge" :class="b.PatientType === 'new' ? 'oa-badge-green' : 'oa-badge-blue'">
                  {{ b.PatientType === 'new' ? 'New' : 'Returning' }}
                </span>
              </td>
              <td>
                <div v-if="b.UTMSource" class="oa-booking-source" :title="getSourceTooltip(b)">
                  <span class="oa-badge oa-badge-source">{{ b.UTMSource }}</span>
                  <span v-if="b.UTMMedium" class="oa-source-medium"> / {{ b.UTMMedium }}</span>
                </div>
                <span v-else class="oa-badge oa-badge-gray">Direct</span>
              </td>
              <td>{{ formatDateTime(b.CreatedAt) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div v-if="totalPages > 1" class="oa-dashboard-pagination">
        <button class="oa-btn oa-btn-secondary oa-btn-sm" :disabled="currentPage <= 1" @click="loadBookings(currentPage - 1)">
          <i class="fa fa-chevron-left"></i> Prev
        </button>
        <span class="oa-pagination-info">Page {{ currentPage }} of {{ totalPages }}</span>
        <button class="oa-btn oa-btn-secondary oa-btn-sm" :disabled="currentPage >= totalPages" @click="loadBookings(currentPage + 1)">
          Next <i class="fa fa-chevron-right"></i>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
export default {
  name: 'OnlineAppointmentsDashboard',
  props: {
    siteID: { type: String, required: true },
    providers: { type: Array, default: () => [] },
    appointmentTypes: { type: Array, default: () => [] }
  },

  data() {
    return {
      stats: {
        totalBookings: 0,
        newPatients: 0,
        thisMonth: 0,
        newPatientPct: 0
      },
      bookings: [],
      loadingBookings: false,
      loadingStats: false,
      searchQuery: '',
      filterType: '',
      filterProvider: '',
      filterSource: '',
      dateFrom: '',
      dateTo: '',
      currentPage: 1,
      pageSize: 25,
      totalPages: 0,
      searchTimeout: null
    };
  },

  created() {
    this.loadStats();
    this.loadBookings(1);
  },

  methods: {
    async loadStats() {
      this.loadingStats = true;
      try {
        const result = await this.$store.dispatch('onlineAppointments/loadBookingStats', {
          siteID: this.siteID
        });
        this.stats = {
          totalBookings: result.totalBookings || 0,
          newPatients: result.newPatients || 0,
          thisMonth: result.thisMonth || 0,
          newPatientPct: result.newPatientPct || 0
        };
      } catch (err) {
        console.error('Error loading booking stats:', err);
      } finally {
        this.loadingStats = false;
      }
    },

    async loadBookings(page) {
      this.loadingBookings = true;
      this.currentPage = page;
      try {
        const result = await this.$store.dispatch('onlineAppointments/loadBookings', {
          siteID: this.siteID,
          page: page,
          pageSize: this.pageSize,
          search: this.searchQuery || undefined,
          appointmentTypeId: this.filterType || undefined,
          providerNum: this.filterProvider || undefined,
          utmSource: this.filterSource || undefined,
          dateFrom: this.dateFrom || undefined,
          dateTo: this.dateTo || undefined
        });
        this.bookings = result.bookings || [];
        this.totalPages = result.totalPages || 0;
      } catch (err) {
        console.error('Error loading bookings:', err);
        this.bookings = [];
      } finally {
        this.loadingBookings = false;
      }
    },

    onSearchInput() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.loadBookings(1);
      }, 400);
    },

    clearSearch() {
      this.searchQuery = '';
      this.loadBookings(1);
    },

    formatDate(dateStr) {
      if (!dateStr) return '—';
      const [y, m, d] = dateStr.split('-');
      const date = new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    },

    formatDateTime(dateStr) {
      if (!dateStr) return '—';
      const d = new Date(dateStr);
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) +
        ' ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    },

    formatTime(timeStr) {
      if (!timeStr) return '—';
      // Already in 12h format (contains AM/PM)
      if (/am|pm/i.test(timeStr)) return timeStr;
      // 24h format — convert to 12h
      const [h, m] = timeStr.split(':');
      const hour = parseInt(h, 10);
      if (isNaN(hour)) return timeStr;
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      return `${hour12}:${m} ${ampm}`;
    },

    getSourceTooltip(b) {
      const parts = [];
      if (b.UTMSource) parts.push('Source: ' + b.UTMSource);
      if (b.UTMMedium) parts.push('Medium: ' + b.UTMMedium);
      if (b.UTMCampaign) parts.push('Campaign: ' + b.UTMCampaign);
      if (b.UTMContent) parts.push('Content: ' + b.UTMContent);
      return parts.join('\n');
    }
  },

  computed: {
    uniqueSources() {
      const sources = this.bookings
        .map(b => b.UTMSource)
        .filter(s => s);
      return [...new Set(sources)].sort();
    }
  },

  watch: {
    siteID(newVal) {
      if (newVal) {
        this.loadStats();
        this.loadBookings(1);
      }
    }
  }
};
</script>
